import { useEffect, useState, type ReactNode } from 'react';
import { BrowserRouter, HashRouter, Navigate, Route, Routes } from 'react-router-dom';
import { DEMO, get, getToken, onUnauthorized, setToken } from './api';
import { AuthCtx, type User } from './auth';
import { startLive, stopLive } from './hooks';
import { ToastProvider, Loading } from './components/ui';
import { Layout } from './components/Layout';
import Login from './pages/Login';
import Inbox from './pages/Inbox';
import Approvals from './pages/Approvals';
import Comments from './pages/Comments';
import Automations from './pages/Automations';
import Knowledge from './pages/Knowledge';
import Playbooks from './pages/Playbooks';
import Compliance from './pages/Compliance';
import Analytics from './pages/Analytics';
import Quality from './pages/Quality';
import WidgetStudio from './pages/WidgetStudio';
import Integrations from './pages/Integrations';
import Settings from './pages/Settings';

export function App() {
  const [user, setUser] = useState<User | null>(null);
  const [checking, setChecking] = useState(true);
  const logout = () => { setToken(null); stopLive(); setUser(null); };

  useEffect(() => {
    const unsub = onUnauthorized(logout); const off = () => { unsub(); };
    if (!getToken()) { setChecking(false); return off; }
    get<{ user: User }>('/auth/me').then((r) => { setUser(r.user); startLive(); }).catch(() => setToken(null)).finally(() => setChecking(false));
    return off;
  }, []);

  const Router = ({ children }: { children: ReactNode }) => DEMO ? <HashRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>{children}</HashRouter> : <BrowserRouter basename="/app" future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>{children}</BrowserRouter>;
  if (checking) return <div className="page"><Loading /></div>;

  return (
    <ToastProvider>
      <AuthCtx.Provider value={{ user, logout }}>
        <Router>
          {!user ? (
            <Routes><Route path="*" element={<Login onLogin={(u) => { setUser(u); startLive(); }} />} /></Routes>
          ) : (
            <Layout>
              <Routes>
                <Route path="/" element={<Navigate to="/inbox" replace />} />
                <Route path="/inbox" element={<Inbox />} />
                <Route path="/inbox/:id" element={<Inbox />} />
                <Route path="/approvals" element={<Approvals />} />
                <Route path="/comments" element={<Comments />} />
                <Route path="/automations" element={<Automations />} />
                <Route path="/knowledge" element={<Knowledge />} />
                <Route path="/playbooks" element={<Playbooks />} />
                <Route path="/compliance" element={<Compliance />} />
                <Route path="/analytics" element={<Analytics />} />
                <Route path="/quality" element={<Quality />} />
                <Route path="/widget" element={<WidgetStudio />} />
                <Route path="/integrations" element={<Integrations />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="*" element={<Navigate to="/inbox" replace />} />
              </Routes>
            </Layout>
          )}
        </Router>
      </AuthCtx.Provider>
    </ToastProvider>
  );
}
